import React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import type { Table as TanStackTable } from "@tanstack/react-table"

type CrudTablePaginationProps<T> = {
  table: TanStackTable<T>
  serverMode?: boolean
  totalCount?: number
  onPageChange?: (pageIndex: number) => void
  onPageSizeChange?: (pageSize: number) => void
}

export function CrudTablePagination<T>({
  table,
  serverMode = false,
  totalCount,
  onPageChange,
  onPageSizeChange,
}: CrudTablePaginationProps<T>) {
  const filteredTotal = serverMode && totalCount !== undefined ? totalCount : table.getFilteredRowModel().rows.length
  const pageIndex = table.getState().pagination.pageIndex
  const pageSize = table.getState().pagination.pageSize
  const pageCount = table.getPageCount()
  const canPrev = table.getCanPreviousPage()
  const canNext = table.getCanNextPage()

  const goPrev = () => {
    table.previousPage()
    if (serverMode && onPageChange) onPageChange(Math.max(0, pageIndex - 1))
  }

  const goNext = () => {
    table.nextPage()
    if (serverMode && onPageChange) onPageChange(pageIndex + 1)
  }

  return (
    <div className="rounded-lg border border-slate-gray/10 bg-white p-3 shadow-sm sm:p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-1 text-sm text-deep-ocean">
          <span className="hidden sm:inline">Showing</span>
          <select
            value={pageSize}
            onChange={(e) => {
              const next = Number(e.target.value)
              table.setPageSize(next)
              if (serverMode && onPageSizeChange) {
                onPageSizeChange(next)
                table.setPageIndex(0)
              }
            }}
            className="mx-0.5 rounded border bg-white p-1.5 text-sm text-deep-ocean focus:outline-none focus:ring-1 focus:ring-electric-blue sm:mx-1"
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-xs sm:text-sm">
            of {filteredTotal} · Page {pageIndex + 1}/{Math.max(pageCount, 1)}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className="hidden h-10 items-center justify-center rounded-lg border border-slate-gray/20 px-3 text-sm font-medium text-deep-ocean enabled:hover:bg-light-sky disabled:cursor-not-allowed disabled:opacity-50 sm:inline-flex"
            onClick={() => {
              table.setPageIndex(0)
              if (serverMode && onPageChange) onPageChange(0)
            }}
            disabled={!canPrev}
          >
            First
          </button>
          <button
            type="button"
            className="inline-flex h-10 min-w-[5rem] flex-1 items-center justify-center gap-1 rounded-lg border border-slate-gray/20 text-sm font-medium text-deep-ocean enabled:hover:bg-light-sky disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none sm:min-w-0 sm:px-3"
            onClick={goPrev}
            disabled={!canPrev}
          >
            <ChevronLeft className="h-4 w-4" />
            Prev
          </button>
          <button
            type="button"
            className="inline-flex h-10 min-w-[5rem] flex-1 items-center justify-center gap-1 rounded-lg bg-electric-blue text-sm font-medium text-white enabled:hover:bg-btn-hover disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none sm:min-w-0 sm:bg-transparent sm:text-deep-ocean sm:hover:bg-light-sky"
            onClick={goNext}
            disabled={!canNext}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
